// The morning work board: OPEN tasks for paying customers, grouped by client with each
// client's owed/overdue money. Strictly work for active clients — the sales pipeline
// lives on its own Leads screen, so the two never conflate. A single all/mine filter,
// so the two views can never disagree.
//
// "בוצע" TAKES A TASK OFF THE BOARD, with five seconds to take it back (DoneUndo). What
// was finished is on the "בוצעו" view — off by default, not remembered — newest first,
// where a task can be reopened or deleted.
//
// THE BOARD STAYS WHERE IT WAS after a save (useStableOrder): the order is taken when the
// view opens or a filter changes, a group whose last task was just done keeps its place,
// and nothing folds by itself.
//
// INTERNAL work (tasks with no client) is one group, always first and always there, so
// its add button is always in reach; the clients / internal / both switch narrows to it.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import useCollapsedGroups from '../../hooks/useCollapsedGroups';
import useStableOrder from '../../hooks/useStableOrder';
import useStoredChoice from '../../hooks/useStoredChoice';
import { tasksApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import EmptyState from '../../components/EmptyState';
import Button from '../../components/Button';
import ClientGroup from '../../components/ClientGroup';
import TaskModal from '../../components/TaskModal';
import ListPanel from '../../components/lists/ListPanel';
import UrgencyFilter from '../../components/UrgencyFilter';
import { DoneUndoProvider } from '../../components/DoneUndo';
import { matchesUrgency } from '../../components/UrgencyBadge';
import { openTasks } from '../../components/TaskList';
import { INTERNAL_KEY } from '../../utils/taskClient';

// Which work the board shows. Applied in the browser over the one board payload, like
// the all/mine and urgency filters beside it, so switching is instant.
const SCOPES = ['all', 'clients', 'internal'];
const SCOPE_LABEL = { all: 'scopeAll', clients: 'scopeClients', internal: 'scopeInternal' };
const STATUS_LABEL = { open: 'statusOpen', done: 'statusDone' };
const isInternalGroup = (g) => g.kind === 'internal';
const inScope = (scope) => (g) => scope === 'all' || (scope === 'internal') === isInternalGroup(g);
// A group's key: the client id, or one fixed key for the internal group.
const groupKey = (g) => (isInternalGroup(g) ? INTERNAL_KEY : g.client.id);
const taskGroupKey = (t) => t.related_client_id ?? INTERNAL_KEY;

export default function Board() {
  const [mine, setMine] = useState(false);
  const [status, setStatus] = useState('open');
  const [scope, setScope] = useStoredChoice('pressbooks.board.scope', SCOPES, 'all');
  // The payload says which view it answers, so the order is reset by the data that
  // arrives for a view, never by stale rows still on screen from the previous one.
  const { data, loading, refreshing, error, reload } = useAsync(
    () => tasksApi.board(mine, 'customers', status).then((groups) => ({ view: `${mine}|${status}`, groups })),
    [mine, status],
  );
  const [listFor, setListFor] = useState(null); // a task whose new list is open
  const [editing, setEditing] = useState(null); // task, {related_client_id}, {} for new, or null
  const [urgency, setUrgency] = useState(null); // null = every urgency
  // Which task's note is open — held here so opening one note closes any other.
  const [editingNoteId, setEditingNoteId] = useState(null);
  const startNote = (id) => setEditingNoteId(id);
  const stopNote = (id) => setEditingNoteId((cur) => (cur === id ? null : cur));
  const { isCollapsed, setCollapsed, setAll } = useCollapsedGroups();
  const done = status === 'done';

  // Same rule as the leads screen: a client qualifies when it holds OPEN work at the
  // chosen level. Filtering therefore also drops the task-less groups that are on the
  // board purely for their money — they have nothing burning by definition. The "בוצעו"
  // view has no urgency filter: nothing on it is burning.
  const filtered = data && data.groups
    .filter(inScope(scope))
    .filter((g) => done || matchesUrgency(g.tasks, urgency));
  const { ordered, keep, firstSeen } = useStableOrder(filtered, {
    keyOf: groupKey,
    resetKey: data && `${data.view}|${scope}|${done ? '' : urgency}`,
    seedOf: (g) => openTasks(g.tasks).length === 0 && !done,
  });

  // Everything is open until the user says otherwise — the spreadsheet this replaced
  // showed the lot at once. The exception is a client with nothing outstanding when the
  // view OPENED: it is on the board for its money alone. That default is read from the
  // first sight of the group, so finishing its last task never folds it by itself.
  const view = (ordered || []).map((g) => {
    const key = groupKey(g);
    return { group: g, key, collapsed: isCollapsed(key, Boolean(firstSeen(key))) };
  });
  // Collapse-all until nothing is left open, then the same control expands.
  const anyOpen = view.some((v) => !v.collapsed);
  const toggleAll = () => setAll(view.map((v) => v.key), anyOpen);

  return (
    <DoneUndoProvider onDone={(t) => keep(taskGroupKey(t))} onUndone={reload}>
      <PageHeader title={he.board.title} spread>
        <div className="seg">
          {['open', 'done'].map((s) => (
            <Button key={s} small variant={status === s ? '' : 'subtle'} onClick={() => setStatus(s)}>
              {he.board[STATUS_LABEL[s]]}
            </Button>
          ))}
        </div>
        <div className="seg">
          <Button small variant={mine ? 'subtle' : ''} onClick={() => setMine(false)}>{he.board.all}</Button>
          <Button small variant={mine ? '' : 'subtle'} onClick={() => setMine(true)}>{he.board.mine}</Button>
        </div>
        <div className="seg">
          {SCOPES.map((s) => (
            <Button key={s} small variant={scope === s ? '' : 'subtle'} onClick={() => setScope(s)}>
              {he.board[SCOPE_LABEL[s]]}
            </Button>
          ))}
        </div>
        {view.length > 1 && (
          <Button small variant="subtle" onClick={toggleAll}>
            {anyOpen ? he.board.collapseAll : he.board.expandAll}
          </Button>
        )}
      </PageHeader>

      <AsyncBoundary loading={loading} error={error}>
        {data && (data.groups.length === 0
          ? <EmptyState text={done ? he.board.emptyDone : he.board.empty} />
          : (
            <>
              {!done && <UrgencyFilter value={urgency} onChange={setUrgency} />}
              {view.length === 0
                ? <EmptyState text={done ? he.board.emptyDone : he.board.noneMatch} />
                : (
                  // Dimmed while reloading — the money on these headers is worth
                  // marking as not-yet-final. The groups stay mounted throughout.
                  <div className={refreshing ? 'refreshing' : undefined}>
                    {view.map(({ group: g, key, collapsed }) => (
                      <ClientGroup
                        key={key}
                        group={g}
                        collapsed={collapsed}
                        onToggle={() => setCollapsed(key, !collapsed)}
                        onChanged={reload}
                        onEdit={(t) => setEditing(t)}
                        // Adding to a folded group unfolds it, so the new task is not
                        // saved into somewhere the user cannot see it land. An internal
                        // group's add hands over no client: the form opens on "פנימי".
                        // The "בוצעו" view adds nothing — a new task is open work.
                        onAdd={done ? null : (c) => { setCollapsed(key, false); setEditing({ related_client_id: c ? c.id : '' }); }}
                        editingNoteId={editingNoteId}
                        onStartNote={startNote}
                        onStopNote={stopNote}
                      />
                    ))}
                  </div>
                )}
            </>
          ))}
      </AsyncBoundary>

      {editing && (
        <TaskModal task={editing} onClose={() => setEditing(null)} onSaved={(r) => { setEditing(null); reload(); if (r?.openListFor) setListFor(r.openListFor); }} />
      )}
      {/* A list made with its task opens at once, ready for the first name. */}
      {listFor && <ListPanel taskId={listFor} onClose={() => setListFor(null)} onChanged={reload} />}
    </DoneUndoProvider>
  );
}
